import { supabase } from './supabase';
import { logger } from './logger';

export interface AdminCandidate {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  status: string;
  registeredAt: string;
  profileComplete: boolean;
  identityVerified: boolean;
  photoUrl: string | null;
  seedTag: string | null;
}

export interface AdminEmployer {
  id: string;
  userId: string;
  companyName: string;
  siret: string;
  siretVerified: boolean;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  subscriptionActive: boolean;
  registeredAt: string;
  jobOffersCount: number;
  seedTag: string | null;
}

export async function fetchCandidates(): Promise<AdminCandidate[]> {
  const { data, error } = await supabase
    .from('candidate_profiles')
    .select(`
      id,
      user_id,
      first_name,
      last_name,
      city,
      profile_complete,
      identity_verified,
      photo_url,
      created_at
    `)
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('fetch_candidates', 'userService', { error: error.message });
    throw error;
  }

  const candidates = data || [];
  logger.technical('fetch_candidates_ok', 'userService', { count: candidates.length });

  const userIds = candidates.map((c: any) => c.user_id).filter(Boolean);
  const profileMap: Record<string, any> = {};
  if (userIds.length > 0) {
    try {
      const { data: profiles, error: profileErr } = await supabase
        .from('profiles')
        .select('id, email, phone, status, seed_tag')
        .in('id', userIds);
      if (profileErr) {
        logger.error('fetch_candidates_profiles', 'userService', { error: profileErr.message });
      }
      (profiles || []).forEach((p: any) => { profileMap[p.id] = p; });
    } catch (err) {
      logger.error('fetch_candidates_profiles_crash', 'userService', { error: String(err) });
    }
  }

  return candidates.map((c: any) => {
    const profile = profileMap[c.user_id];
    return {
      id: c.id,
      userId: c.user_id,
      firstName: c.first_name || '',
      lastName: c.last_name || '',
      email: profile?.email || '',
      phone: profile?.phone || '',
      city: c.city || '',
      status: profile?.status || 'pending_verification',
      registeredAt: new Date(c.created_at).toLocaleDateString('fr-FR'),
      profileComplete: c.profile_complete || false,
      identityVerified: c.identity_verified || false,
      photoUrl: c.photo_url,
      seedTag: profile?.seed_tag || null,
    };
  });
}

export async function fetchEmployers(): Promise<AdminEmployer[]> {
  const { data, error } = await supabase
    .from('employer_profiles')
    .select(`
      id,
      user_id,
      first_name,
      last_name,
      company_name,
      siret,
      siret_verified,
      email,
      phone,
      company_city,
      subscription_active,
      created_at
    `)
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('fetch_employers', 'userService', { error: error.message });
    throw error;
  }

  const employers = data || [];
  logger.technical('fetch_employers_ok', 'userService', { count: employers.length });

  const counts: Record<string, number> = {};
  const { data: offers } = await supabase
    .from('job_offers')
    .select('employer_id');
  (offers || []).forEach((o: any) => {
    counts[o.employer_id] = (counts[o.employer_id] || 0) + 1;
  });

  const empUserIds = employers.map((e: any) => e.user_id).filter(Boolean);
  const seedMap: Record<string, string | null> = {};
  if (empUserIds.length > 0) {
    try {
      const { data: profiles, error: profErr } = await supabase
        .from('profiles')
        .select('id, seed_tag')
        .in('id', empUserIds);
      if (profErr) {
        logger.error('fetch_employers_profiles', 'userService', { error: profErr.message });
      }
      (profiles || []).forEach((p: any) => { seedMap[p.id] = p.seed_tag; });
    } catch (err) {
      logger.error('fetch_employers_profiles_crash', 'userService', { error: String(err) });
    }
  }

  return employers.map((e: any) => ({
    id: e.id,
    userId: e.user_id,
    companyName: e.company_name || '',
    siret: e.siret || '',
    siretVerified: e.siret_verified || false,
    firstName: e.first_name || '',
    lastName: e.last_name || '',
    email: e.email || '',
    phone: e.phone || '',
    city: e.company_city || '',
    subscriptionActive: e.subscription_active || false,
    registeredAt: new Date(e.created_at).toLocaleDateString('fr-FR'),
    jobOffersCount: counts[e.id] || 0,
    seedTag: seedMap[e.user_id] || null,
  }));
}

export async function disableAccount(userId: string, disabled: boolean): Promise<void> {
  logger.functional(disabled ? 'disable_account' : 'enable_account', 'userService', { userId });
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-disable-account`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ userId, disabled }),
    }
  );

  const body = await res.json();
  if (!res.ok) {
    logger.error('disable_account', 'userService', { userId, error: body.error, status: res.status });
    throw new Error(body.error || 'Failed');
  }
  logger.technical('disable_account_ok', 'userService', { userId, status: body.status });
}

export async function resetPassword(userId: string, newPassword: string): Promise<void> {
  logger.functional('reset_password', 'userService', { userId });
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-reset-password`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ userId, newPassword }),
    }
  );

  const body = await res.json();
  if (!res.ok) {
    logger.error('reset_password', 'userService', { userId, error: body.error, status: res.status });
    throw new Error(body.error || 'Failed');
  }
  logger.technical('reset_password_ok', 'userService', { userId });
}
